import uniqueId from 'lodash-es/uniqueId'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { getItemsIds } from '../../../utils/common'
import { formatDateToApi } from '../../../utils/date'
import { formValidationSchemaNoChanged } from '../../Form'

import {
  CONNECTED_REQUESTS_MASS_EDIT_ACTIONS,
  CONNECTED_REQUESTS_MASS_EDIT_EMPTY_PARAMETER,
  CONNECTED_REQUESTS_MASS_EDIT_FIELDS,
  CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES,
  CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES,
  CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_ROW_ID_PREFIX,
} from './ConnectedRequestsMassEditModal.constants'

function getParameterRowId(parameter) {
  return (
    parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.rowId] ||
    uniqueId(CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_ROW_ID_PREFIX)
  )
}

function getNormalizedParameter(parameter = EMPTY_OBJECT) {
  return {
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.action]:
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.action] ??
      null,
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.comment]:
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.comment] ??
      CONNECTED_REQUESTS_MASS_EDIT_EMPTY_PARAMETER[
        CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.comment
      ],
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.field]:
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.field] ??
      null,
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.isComment]:
      parameter?.[
        CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.isComment
      ] ??
      CONNECTED_REQUESTS_MASS_EDIT_EMPTY_PARAMETER[
        CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.isComment
      ],
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.rowId]:
      getParameterRowId(parameter),
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.template]:
      parameter?.[
        CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.template
      ] ??
      CONNECTED_REQUESTS_MASS_EDIT_EMPTY_PARAMETER[
        CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.template
      ],
    [CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.value]:
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.value] ??
      null,
  }
}

function isDateTimeField(fieldValue) {
  return fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.deadline
}

function isMultiValueField(fieldValue) {
  return (
    fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.managers ||
    fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.providers ||
    fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.labels
  )
}

function shouldAddCommentToPayload({ actionValue, fieldValue, parameter }) {
  return (
    shouldShowInvitationField({ actionValue, fieldValue }) &&
    Boolean(
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.isComment]
    )
  )
}

function getPayloadValue({ fieldValue, timezone, value }) {
  if (isDateTimeField(fieldValue)) {
    return formatDateToApi(value, timezone)
  }

  if (isMultiValueField(fieldValue)) {
    return getItemsIds(
      Array.isArray(value) ? value : EMPTY_LIST,
      'value'
    ).filter(Boolean)
  }

  return value?.value ?? null
}

function hasPayloadValue({ fieldValue, preparedValue }) {
  if (isMultiValueField(fieldValue)) {
    return preparedValue.length > 0
  }

  return preparedValue != null
}

function getPreparedParameterPayload({ parameter, timezone }) {
  const fieldValue =
    parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.field]?.value
  const actionValue =
    parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.action]
      ?.value

  if (!fieldValue || !actionValue) {
    return null
  }

  if (!shouldShowValueField(actionValue)) {
    return {
      key: fieldValue,
      value: {
        command: actionValue,
      },
    }
  }

  const preparedValue = getPayloadValue({
    fieldValue,
    timezone,
    value:
      parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.value],
  })

  if (!hasPayloadValue({ fieldValue, preparedValue })) {
    return null
  }

  const preparedPayloadValue = {
    command: actionValue,
    value: preparedValue,
  }

  if (shouldAddCommentToPayload({ actionValue, fieldValue, parameter })) {
    preparedPayloadValue[
      CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.comment
    ] = parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.comment]
  }

  return {
    key: fieldValue,
    value: preparedPayloadValue,
  }
}

function getPreparedPayload({ timezone, values }) {
  const parameters =
    values?.[CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.parameters] ||
    EMPTY_LIST

  return parameters.reduce((acc, parameter) => {
    const preparedParameter = getPreparedParameterPayload({
      parameter,
      timezone,
    })

    if (preparedParameter) {
      acc[preparedParameter.key] = preparedParameter.value
    }

    return acc
  }, {})
}

function isConnectedRequestsMassEditPayloadEmpty({ timezone, values }) {
  return Object.keys(getPreparedPayload({ timezone, values })).length === 0
}

export function shouldShowValueField(actionValue) {
  return (
    Boolean(actionValue) &&
    actionValue !== CONNECTED_REQUESTS_MASS_EDIT_ACTIONS.delete
  )
}

export function createEmptyParameter() {
  return getNormalizedParameter()
}

export function getCanAddParameter(parameters = EMPTY_LIST) {
  return (
    parameters.length < Object.keys(CONNECTED_REQUESTS_MASS_EDIT_FIELDS).length
  )
}

export function getPreparedInitialValues(initialValues) {
  const preparedParameterList = (
    initialValues?.[CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.parameters] ??
    EMPTY_LIST
  ).map(getNormalizedParameter)

  return {
    [CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.excludeArchived]:
      initialValues?.[
        CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.excludeArchived
      ] ?? false,
    [CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.parameters]: [
      ...(preparedParameterList.length > 0
        ? preparedParameterList
        : [createEmptyParameter()]),
    ],
  }
}

export function shouldShowInvitationField({ actionValue, fieldValue }) {
  return (
    actionValue &&
    actionValue !== CONNECTED_REQUESTS_MASS_EDIT_ACTIONS.delete &&
    (fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.managers ||
      fieldValue === CONNECTED_REQUESTS_MASS_EDIT_FIELDS.providers)
  )
}

export function getSubmitPayload({ timezone, values }) {
  const payload = getPreparedPayload({ timezone, values })

  return {
    exclude_archived:
      values[CONNECTED_REQUESTS_MASS_EDIT_FORM_FIELD_NAMES.excludeArchived],
    payload,
  }
}

export function getConnectedRequestsMassEditNotificationTitle({ count, t }) {
  return t('Notification.CommonTitleMassSavedKey', {
    count,
    totalCount: count,
  })
}

export function getQaNameConnectedRequestsMassEditElement(name, index) {
  return `${name}_${index}`
}

export function getValidationSchema({ timezone }) {
  return formValidationSchemaNoChanged({
    initialValues: EMPTY_OBJECT,
    isEqual: ({ values }) =>
      isConnectedRequestsMassEditPayloadEmpty({ timezone, values }),
  })
}
